import { useEffect, useState } from 'react'
import { useEditorContext } from './hooks/useEditorContext'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  MarkdownEditor,
  MarkdownProvider,
  Message,
  Select,
} from '@workwhile/ui'
import { Paginator } from './Paginator'
import { Preview } from './Preview'
import { useDebounceFn } from 'ahooks'
import { SaveBox } from './SaveBox'
import { TutorialCategory } from 'api/shift'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useCompanyFiles } from 'queries/company/useCompanyFiles'
import { getPdfPath } from './utils'
import { useGateValue } from '@statsig/react-bindings'

const ContentCard = ({
  header,
  body,
  buttonLabel,
  onClick,
}: {
  header: string
  body: string
  buttonLabel: string
  onClick: () => void
}) => {
  return (
    <Card
      p={4}
      m={3}
      width={320}
      boxShadow="md"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box
        width="100%"
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
      >
        <Heading level={4} mb={2} textAlign="center">
          {header}
        </Heading>
        <Box mb={3} textAlign="center">
          {body}
        </Box>
      </Box>
      <Box width="100%" mt={3} display="flex" justifyContent="center">
        <Button variant="primary" onClick={onClick}>
          {buttonLabel}
        </Button>
      </Box>
    </Card>
  )
}

export function ContentEditor() {
  const {
    tutorialId,
    content,
    category,
    setContent,
    quiz,
    generateQuiz,
    editorDefaultValue,
    currentPageIdx,
    setCurrentPageIdx,
    setTabValue,
  } = useEditorContext()
  const enableSignature = category === TutorialCategory.General
  const canAttachPdf = useGateValue(FeatureGates.EnablePDF)
  const page = content[currentPageIdx].markdown.trim()
  const pdfPath = getPdfPath(page)
  const [editorType, setEditorType] = useState<'markdown' | 'pdf' | null>(null)

  useEffect(() => {
    setEditorType(null)
  }, [page])

  const { data: files } = useCompanyFiles({
    type: 'pdf',
    includes: ['temp_url'],
    prefix: 'public',
  })
  const pdfOptions = files?.map((file) => ({
    label: file.path.split('/').pop() || '',
    value: file.path,
    isDisabled: content.some((c) => c.markdown.trim() === file.path),
    ...file,
  }))
  const selectedPdf = pdfOptions?.find((option) => option.value === pdfPath)

  const { run: onEditorChange } = useDebounceFn(
    (value: string) => {
      setContent((prevContent) =>
        prevContent.map((c, idx) => {
          if (idx === currentPageIdx) {
            return { ...c, markdown: value }
          }
          return c
        })
      )
    },
    { wait: 300 }
  )

  const handleRequireSignatureChange = (checked: boolean) => {
    setContent((prevContent) =>
      prevContent.map((c, idx) => {
        if (idx === currentPageIdx && c.require_signature !== checked) {
          return { ...c, require_signature: checked }
        }
        return c
      })
    )
  }

  const showContentTypeOptions =
    enableSignature && canAttachPdf && !editorType && !page
  const showMarkdownEditor =
    (!showContentTypeOptions && !selectedPdf && editorType !== 'pdf') ||
    editorType === 'markdown'

  return (
    <>
      <Heading level={3}>Edit Your Instruction</Heading>
      <Flex>
        <Box flex={1} mr={[0, 0, 0, 4, 7]}>
          <Paginator
            pages={content}
            currentPageIdx={currentPageIdx}
            onPageChange={setCurrentPageIdx}
            onAddPage={() => {
              setContent([...content, { markdown: '' }])
              setCurrentPageIdx(content.length)
            }}
            onDeletePage={(pageIdx) => {
              const newContent = content.filter((_, idx) => idx !== pageIdx)
              setContent(newContent)
              if (currentPageIdx >= newContent.length - 1) {
                setCurrentPageIdx(newContent.length - 1)
              }
            }}
          />

          {showContentTypeOptions ? (
            <Flex mb={4}>
              <ContentCard
                header="Blank Page"
                body={
                  'Add custom tutorial content (e.g. Text, Images, Videos, Links)'
                }
                buttonLabel="Add Blank Page"
                onClick={() => {
                  setEditorType('markdown')
                }}
              />
              <ContentCard
                header="Document Signing"
                body={'Attach a PDF for workers to sign.'}
                buttonLabel="Add Document Signing"
                onClick={() => {
                  setEditorType('pdf')
                }}
              />
            </Flex>
          ) : null}

          {showMarkdownEditor ? (
            <MarkdownProvider key={`page-${currentPageIdx}`}>
              <MarkdownEditor
                minHeight={400}
                defaultValue={editorDefaultValue}
                onChange={onEditorChange}
              />
              {enableSignature ? (
                <Box mt={4}>
                  <Checkbox
                    defaultChecked={
                      !!content[currentPageIdx]?.require_signature
                    }
                    onChange={handleRequireSignatureChange}
                  >
                    Require Worker's Signature
                  </Checkbox>
                </Box>
              ) : null}
            </MarkdownProvider>
          ) : null}

          {(!!selectedPdf || editorType === 'pdf') && (
            <Flex gap={2} flexDirection="column">
              <Select
                key={selectedPdf?.value}
                options={pdfOptions}
                onChange={(option) => {
                  if (option?.value) {
                    setContent((prevContent) =>
                      prevContent.map((c, idx) =>
                        idx === currentPageIdx
                          ? {
                              ...c,
                              markdown: option.value as string,
                              require_signature: true,
                            }
                          : c
                      )
                    )
                  }
                }}
                defaultValue={selectedPdf}
                placeholder={
                  !!selectedPdf
                    ? 'Change selected PDF file'
                    : 'Select a PDF file'
                }
                isSearchable
              />
              <Box mt={2} color="neutrals.500" fontSize={2}>
                To add additional PDFs, please contact your Account Manager.
              </Box>
              {!!selectedPdf && (
                <Box
                  style={{
                    width: '100%',
                    aspectRatio: '8.5/11',
                    minHeight: 400,
                  }}
                >
                  <object
                    data={selectedPdf?.tempUrl}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{
                      display: 'block',
                      border: '1px solid #eee',
                      borderRadius: 4,
                    }}
                  />
                </Box>
              )}
            </Flex>
          )}

          {/* quiz is not supported on creation, only edit */}
          {!quiz && tutorialId ? (
            <Message mt={4} variant={'info'}>
              Want to make sure workers understand requirements when accepting
              the shift?&nbsp;
              <a
                onClick={() => {
                  generateQuiz()
                  setTabValue('quiz')
                }}
              >
                Auto Generate Quiz
              </a>
            </Message>
          ) : null}
          <SaveBox />
        </Box>
        <Box display={['none', 'none', 'none', 'block']}>
          <Box position={'sticky'} top={60}>
            <Preview
              content={content[currentPageIdx]?.markdown}
              requireSignature={!!content[currentPageIdx]?.require_signature}
            />
          </Box>
        </Box>
      </Flex>
    </>
  )
}
